<template>
  <div class="card mgt-20px">
    <header class="header-card">
      <p>{{ $t('ajoutDevis.etatSuivi.etatsDeSuivi')}}</p>
    </header>
    <div class="card-content">
      <div v-if="isEdit" class="columns is-marginless">
        <div class="column is-narrow">
          <selecteur-action
            :options="actions"
            :placeholder='$t("general.placeHolder.groupedAction")'
            :elementsSelectionnes="getAllSelected.length > 0"
            titleButton="general.button.apply">
          </selecteur-action>
        </div>
        <div class="column has-text-right">
          <a @click="addEtat()" class="button is-addOrEdit">
            {{ $t('ajoutDevis.etatSuivi.ajouterEtat')}}
          </a>
        </div>
      </div>
      <v-client-table
        :data="dcfEtatSuivis"
        :columns="columns"
        :options="options"
        class="column is-12"
        ref="tableEtatSuiviDcf">
        <template slot="selected" slot-scope="props">
          <div class="has-text-centered">
            <qd-checkbox class="mgt-5px"
              v-model="props.row.selected"
              @input="updateEtat(props.row, 'selected')"/>
          </div>
        </template>
        <template slot="etatSuiviId" slot-scope="props">
          <parametre-autocomplete
            v-if="isEdit"
            v-model="props.row.etatSuiviId"
            :target="constantes.PARAMETRES_KEYS.etatSuiviAdministratif"
            @input="updateEtat(props.row, 'etatSuiviId')"
            expanded
          />
          <span v-else> {{ getLibelleInListe(props.row.etatSuiviId, etatSuivis) }} </span>
        </template>
        <template slot="date" slot-scope="props">
          <b-datepicker
            v-if="isEdit"
            v-model="props.row.date"
            icon="calendar-today"
            @input="updateEtat(props.row, 'date')"
            show-week-number
            nearby-selectable-month-days
            editable>
          </b-datepicker>
          <span v-else> {{ formatDate(props.row.date) }} </span>
        </template>
        <template slot="delete" slot-scope="props">
          <a @click="deleteEtat(props.row.id)">
            <b-icon icon="delete-forever" class="mgt-5px" type="is-danger"/>
          </a>
        </template>
      </v-client-table>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import * as tableConfig from '@/configuration/clientTableConf';
import * as typesParametres from '@/store/modules/parametres-mutation-type';
import messageErreur from '@/services/general/messageErreur';
import { columnsSplice } from '@/services/general/columns';
import formatDate from '@/services/general/formatDate';
import selecteurAction from '@/components/general/selecteurAction';
import genererEtatSuiviDevisCommande from '@/services/devisCommandes/genererEtatSuiviDevisCommande';
import ParametreAutocomplete from '@/components/general/autocomplete/parametreAutocomplete';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';

const Parametres = createNamespacedHelpers('parametres');

export default {
  props: {
    value: {
      type: Array,
      required: true,
    },
    isEdit: {
      type: Boolean,
      required: false,
    },
  },
  components: {
    selecteurAction,
    ParametreAutocomplete,
  },
  data() {
    return {
      actions: [],
      constantes: {
        PARAMETRES_KEYS,
      },
    };
  },
  computed: {
    ...Parametres.mapGetters({
      parametre: 'parametre',
    }),
    dcfEtatSuivis: {
      get() {
        return this.value;
      },
      set(newVal) {
        this.$emait('input', newVal);
      },
    },
    getAllSelected() {
      return this.dcfEtatSuivis.filter((el) => el.selected);
    },
    iconSelectTotal() {
      if (this.allSelected) return 'checkbox-multiple-marked-outline';
      return 'checkbox-multiple-blank-outline';
    },
    allSelected() {
      return this.getAllSelected.length === this.dcfEtatSuivis.length
        && this.dcfEtatSuivis.length > 0;
    },
    options() {
      const texts = cloneDeep(tableConfig.TEXTS);
      texts.count = '';
      return {
        sortIcon: tableConfig.SORT_ICON,
        headings: {
          etatSuiviId: this.$t('ajoutDevis.etatSuivi.etatDeSuivi'),
          date: this.$t('ajoutDevis.etatSuivi.date'),
          delete: '',
          selected: (h) => {
            return h(
              'a', {
                attrs: {
                  class: 'pointer',
                },
                on: {
                  click: () => {
                    this.selectAll();
                  },
                },
              },
              [
                h('b-icon', {
                  attrs: {
                    icon: this.iconSelectTotal,
                  },
                }),
              ],
            );
          },
        },
        filterable: false,
        perPageValues: tableConfig.NO_PER_PAGE_VALUE,
        perPage: tableConfig.RESULTS_PER_PAGE,
        texts: texts,
        columnsClasses: {
          selected: 'colonneSelected',
          delete: 'colonneSelected',
        },
        sortable: [],
      };
    },
    columns() {
      const columns = ['selected', 'etatSuiviId', 'date', 'delete'];
      if (!this.isEdit) {
        columnsSplice(columns, 'selected');
        columnsSplice(columns, 'delete');
      }
      return columns;
    },
    etatSuivis() {
      return this.parametre(typesParametres.PARAMETRES_KEYS.etatSuiviAdministratif);
    },
  },
  methods: {
    ...Parametres.mapActions({
      loadParametres: 'loadParametres',
    }),
    selectAll() {
      const selected = !this.allSelected;
      this.dcfEtatSuivis.forEach((el) => {
        el.selected = selected;
      });
    },
    addEtat() {
      this.dcfEtatSuivis.push(genererEtatSuiviDevisCommande());
    },
    updateEtat(row, key) {
      const find = this.dcfEtatSuivis.find(el => el.id === row.id);
      if (find) this.$set(find, key, row[key]);
    },
    deleteEtat(id) {
      const index = this.dcfEtatSuivis.findIndex(el => el.id === id);
      this.dcfEtatSuivis.splice(index, 1);
    },
    getLibelleInListe(id, liste) {
      const find = liste.find(el => el.id === id);
      if (find) return find.libelle;
      return '';
    },
    formatDate(date) {
      return formatDate(date);
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.loadParametres([
        typesParametres.PARAMETRES_KEYS.etatSuiviAdministratif,
      ]);
      loadingComponent.close();
    } catch (e) {
      messageErreur(this.$i18n);
      loadingComponent.close();
      throw e;
    }
  },
};
</script>
